import moment from 'moment';
import qwest from 'qwest';
import session from '../services/sessionService.jsx';
import Customer from '../models/customer.jsx';
import Purchase from '../models/purchase.jsx';
import ProductProfile from '../models/productProfiles.jsx';
import CustomerStatistics from '../models/customerStatistics.jsx';
import PurchaseSalesHistory from '../models/PurchaseSalesHistory.jsx';

let searchHistory = [];

let onSuccess = () => {
  return (xhr, response) => {
    if (response.success) {
      return response.data;
    }
    throw response;
  };
};

let onError = () => {
  throw {
    code: -1,
    data: null,
    message: 'Unexpected Error',
    success: false,
  };
};

let CustomerApi = {
  getCustomers(filter, page) {
    searchHistory.push(filter);

    let onSuccess = (xhr, response) => {
      let index = searchHistory.lastIndexOf(filter);

      if (response.success && index !== -1) {
        // Remove all searches prior to this one
        for (let i = 0; i <= index; i++) {
          searchHistory.shift(filter);
        }
        return response.data;
      }
      throw response;
    };

    let onError = () => {
      throw {
        code: -1,
        data: null,
        message: 'Unexpected Error',
        success: false,
      };
    };

    if (!page) {
      page = 1;
    }

    return qwest.get(`/Customer?page=${page}&filter=${filter}`)
      .then(onSuccess, onError);
  },

  getCustomerDuplicates(filter) {
    searchHistory.push(filter);

    let onSuccess = (xhr, response) => {
      let index = searchHistory.lastIndexOf(filter);

      if (response.success && index !== -1) {
        // Remove all searches prior to this one
        for (let i = 0; i <= index; i++) {
          searchHistory.shift(filter);
        }
        return response.data;
      }
      throw response;
    };

    let onError = () => {
      throw {
        code: -1,
        data: null,
        message: 'Unexpected Error',
        success: false,
      };
    };

    return qwest.get(`/Customer/DuplicateCustomerSearch?filter=${filter}`)
      .then(onSuccess, onError);
  },

  getCustomer(id, forceNew = true) {
    return new Promise((resolve, reject) => {
      let customer = session.getCustomer(id);

      if (customer && !forceNew) {
        resolve(customer);
      } else {
        let dealerID = session.getUser().dealerId;

        qwest.get(`/Customer?id=${id}&dealerId=${dealerID}`)
          .then((xhr, response) => {
            if (response.success) {
              customer = Customer.fromApiFormat(response.data);
              session.setCustomer(id, customer);
              resolve(customer);
            } else {
              reject(response);
            }
          });
      }
    });
  },

  getCustomerRecentInvoiceHistory(id) {
    return new Promise((resolve, reject) => {
      qwest.get(`/Customer/GetCustomerRecentInvoiceHistory?id=${id}`)
        .then((xhr, response) => {
          if (response.success) {
            var data = response.data;

            data.LastPurchaseDate = moment(data.LastPurchaseDate, 'YYYY-MM-DD h-mm-ss a');
            resolve(data);
          } else {
            reject(response);
          }
        });
    });
  },

  checkUniqueCustomer(id, address) {
    return new Promise((resolve, reject) => {
      qwest.post('/Customer/CheckUniqueCustomer', {
        id: id,
        address: address,
      })
        .then((xhr, response) => {
          if (response.success) {
            resolve(response.data);
          } else {
            reject(response);
          }
        });
    });
  },

  checkUniqueContact(id, phone, email) {
    return new Promise((resolve, reject) => {
      qwest.post('/Customer/CheckUniqueContact', {
        id: id,
        email: email,
        phone: phone,
      })
        .then((xhr, response) => {
          if (response.success) {
            resolve(response.data);
          } else {
            reject(response);
          }
        });
    });
  },

  getNextAccountNumber() {
    return new Promise((resolve, reject) => {
      qwest.get('/Customer/NextAccountNumber')
        .then((xhr, response) => {
          if (response.success) {
            resolve(response.data);
          } else {
            reject(response);
          }
        });
    });
  },

  saveCustomer(customer) {
    console.log('in save customer');
    let dealerId = session.getUser().dealerId;

    return new Promise((resolve, reject) => {
      qwest.post(`/Customer?dealerId=${dealerId}`, customer.toApiFormat())
        .then((xhr, response) => {
          if (response.success) {
            let customer = Customer.fromApiFormat(response.data);

            session.setCustomer(customer.id, customer);
            resolve(customer);
          } else {
            reject(response);
          }
        });
    });
  },

  deleteCustomer(customer) {
    return new Promise((resolve, reject) => {
      qwest.delete('/Customer', customer.toApiFormat())
        .then((xhr, response) => {
          if (response.success) {
            session.deleteCustomer(customer);
            resolve(response.data);
          } else {
            reject(response);
          }
        });
    });
  },

  inactiveCustomersByLastAnalysis(months = 12, setInactive = false) {
    return new Promise((resolve, reject) => {
      qwest.get(`/Customer/CustomersByAnalysisDate?months=${months}&makeInactive=${setInactive}`)
        .then((xhr, response) => {
          if (response.success) {
            resolve(response.data);
          } else {
            reject(response);
          }
        });
    });
  },

  inactiveCustomersBySystem(setInactive = false) {
    return new Promise((resolve, reject) => {
      qwest.get(`/Customer/CustomersNoSystems?makeInactive=${setInactive}`)
        .then((xhr, response) => {
          if (response.success) {
            resolve(response.data);
          } else {
            reject(response);
          }
        });
    });
  },

  getCustomerStatistics(id) {
    return new Promise((resolve, reject) => {
      qwest.get(`/Customer/CustomerDashboardStatistics?id=${id}`)
        .then((xhr, response) => {
          if (response.success) {
            let customerStatistics = CustomerStatistics.fromApiFormat(response.data);

            resolve(customerStatistics);
          } else {
            reject(response);
          }
        });
    });
  },

  getCustomerPurchaseSummary(id, dateRange, dateFrom, dateTo) {
    return new Promise((resolve, reject) => {
      qwest.get(`/customer/CustomerPurchaseHistory?id=${id}&dateRange=${dateRange}&dateFrom=${dateFrom}&dateTo=${dateTo}`)
        .then((xhr, response) => {
          if (response.success) {
            resolve(Purchase.fromApiFormat(response.data));
          } else {
            reject(response);
          }
        });
    });
  },

  getCustomerPurchaseSalesHistory(id, dateRange) {
    return new Promise((resolve, reject) => {
      qwest.get(`/customer/GetPurchasedVsRecommendedStatistics?id=${id}&dateRange=${dateRange}`)
        .then((xhr, response) => {
          if (response.success) {
            var res = [];

            res.MaintanenceProducts = PurchaseSalesHistory.fromApiFormat(response.data.MaintanenceProducts);
            res.OtherProducts = PurchaseSalesHistory.fromApiFormat(response.data.OtherProducts);
            res.ProductProfiles = ProductProfile.fromApiFormat(response.data.ProductProfiles);
            resolve(res);
          } else {
            reject(response);
          }
        });
    });
  },

  getMissingInformation(store,systemType,accountStatus,missingInformation) {
    return qwest.get(`v1/newReports/Customer/MissingInformation/Summary?store=${store}` +
      `&systemType=${systemType}&accountStatus=${accountStatus}&missingInformation=${missingInformation}`)
      .then(onSuccess(), onError);
  },

  exportMissingInformationReport(locale, store, systemType, accountStatus, missingInformation) {
    window.open(`v1/newReports/Customer/MissingInformation/ExportReport?store=${store}` +
      `&systemType=${systemType}&accountStatus=${accountStatus}&missingInformation=${missingInformation}&locale=${locale}`);
  },

  getCustomerPurchaseDetails(id) {
    return new Promise((resolve, reject) => {
      qwest.get(`/customer/purchaseDetails?id=${id}`)
        .then((xhr, response) => {
          if (response.success) {
            resolve(response.data);
          } else {
            reject(response);
          }
        });
    });
  },

  purchaseDetailsExport(id) {
    window.open(`/customer/purchaseDetailsExport?id=${id}`);
  },

  getTruBlueSummaryReport(from, to, status, store) {
    return qwest.get(`v1/newReports/Customer/TruBlue/Summary?start=${from}&end=${to}&status=${status}&branch=${store}`)
      .then(onSuccess(), onError);
  },

  exportTruBlueSummaryReport(locale, store, status, from, to) {
    window.open(`v1/newReports/Customer/TruBlue/ExportReport?branch=${store}&status=${status}&start=${from}&end=${to}&locale=${locale}`);
  },

  getAccountSystemListSummary(from, to, store, systemType, groundLevel, sanitazer, accountStatus) {
    return qwest.get(`v1/newReports/Customer/AccountSystemList/Summary?from=${from}` +
      `&to=${to}&branch=${store}&systemType=${systemType}&groundLevel=${groundLevel}&sanitazer=${sanitazer}&accountStatus=${accountStatus}`)
        .then(onSuccess(), onError);
  },

  getAccountSystemListExport(locale, from, to, store, systemType, groundLevel, sanitazer, accountStatus) {
    window.open(`v1/newReports/Customer/AccountSystemList/AccountListExport?from=${from}&to=${to}` +
      `&branch=${store}&systemType=${systemType}&groundLevel=${groundLevel}&sanitazer=${sanitazer}&accountStatus=${accountStatus}&locale=${locale}`);
  },

  checkUniqueTruBlueMemberID(id, memberID) {
    return new Promise((resolve, reject) => {
      qwest.post('/Customer/CheckUniqueTruBlueMemberID', {
        id,
        memberID,
      })
        .then((xhr, response) => {
          if (response.success) {
            resolve(response.data);
          } else {
            reject(response);
          }
        });
    });
  },


};

export default CustomerApi;
